import { useAuthStore } from '@/auth/authStore';
import { SERVER_URL, COMMON_HEADERS } from '@/config/serverUrl';
import type { RankTier } from '@shared/types';

const BASE = SERVER_URL;

async function req<T>(method: string, path: string, body?: unknown): Promise<T> {
  const token = useAuthStore.getState().token;
  const res = await fetch(`${BASE}${path}`, {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...COMMON_HEADERS,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: body == null ? undefined : JSON.stringify(body),
    credentials: 'include',
  });
  if (!res.ok) throw new Error(`Social API ${res.status}`);
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export interface AvatarFrameSpec {
  border?: string | null;
  glow?: string | null;
  /** Transparent PNG ring overlay — wins over border/glow when set. */
  imageUrl?: string | null;
}

export interface FriendEntry {
  userId: string;
  displayName: string;
  avatarUrl: string | null;
  avatarColor: 'red' | 'blue';
  rank: RankTier;
  rankDivision: number;
  mmr: number;
  online: boolean;
  sinceMs: number;
  frame?: AvatarFrameSpec | null;
  characterIdleUrl?: string | null;
}

export interface ChatSender {
  id: string;
  displayName: string;
  avatarUrl: string | null;
  avatarColor: 'red' | 'blue';
  rank: RankTier;
  frame?: AvatarFrameSpec | null;
  characterIdleUrl?: string | null;
}

export interface GlobalMessage {
  id: string;
  text: string;
  createdAt: string;
  sender: ChatSender;
}

export interface DmMessage {
  id: string;
  text: string;
  fromId: string;
  toId: string;
  createdAt: string;
}

export type FriendshipStatus = 'self' | 'none' | 'pending-out' | 'pending-in' | 'accepted';

export interface DmThread {
  peer: {
    id: string;
    displayName: string;
    avatarUrl: string | null;
    avatarColor: 'red' | 'blue';
    rank: RankTier;
    rankDivision: number;
    mmr: number;
    online: boolean;
    frame?: AvatarFrameSpec | null;
    characterIdleUrl?: string | null;
  };
  lastMessage: {
    text: string;
    createdAt: string;
    fromId: string;
    mine: boolean;
  };
  unread: number;
}

export interface UserPeek {
  user: {
    id: string;
    displayName: string;
    avatarUrl: string | null;
    avatarColor: 'red' | 'blue';
    rank: RankTier;
    rankDivision: number;
    mmr: number;
    matchesPlayed: number;
    matchesWon: number;
    winStreak: number;
    online: boolean;
    frame?: AvatarFrameSpec | null;
    characterIdleUrl?: string | null;
  };
  friendshipStatus: FriendshipStatus;
  blocked?: boolean;
}

// In-memory cache of the last peek per user. Lets the quick-menu render its
// avatar + stats instantly on reopen while a fresh fetch revalidates in the
// background, instead of blocking on a network round-trip every time.
const peekCache = new Map<string, UserPeek>();

export const socialApi = {
  list: () =>
    req<{
      friends: FriendEntry[];
      incoming: FriendEntry[];
      outgoing: FriendEntry[];
    }>('GET', '/friends/'),
  requestByUsername: (username: string) =>
    req<{ accepted: boolean }>('POST', '/friends/request', { username }),
  requestById: (userId: string) =>
    req<{ accepted: boolean }>('POST', '/friends/request', { userId }),
  peek: async (userId: string) => {
    const data = await req<UserPeek>('GET', `/friends/peek/${userId}`);
    peekCache.set(userId, data);
    return data;
  },
  /** Synchronous read of the cached peek (or null) for instant first paint. */
  peekCached: (userId: string): UserPeek | null => peekCache.get(userId) ?? null,
  accept: (userId: string) =>
    req<{ ok: true }>('POST', `/friends/${userId}/accept`),
  remove: (userId: string) =>
    req<{ ok: true }>('DELETE', `/friends/${userId}`),

  globalHistory: () =>
    req<{ messages: GlobalMessage[] }>('GET', '/chat/global'),
  threads: () =>
    req<{ threads: DmThread[] }>('GET', '/chat/threads'),
  sendGlobal: (text: string) =>
    req<{ message: GlobalMessage }>('POST', '/chat/global', { text }),

  dmHistory: (userId: string) =>
    req<{ messages: DmMessage[] }>('GET', `/chat/dm/${userId}`),
  sendDm: (userId: string, text: string) =>
    req<{ message: DmMessage }>('POST', `/chat/dm/${userId}`, { text }),

  // ─── Block / report (App Store Guideline 1.2) ──────────────────────
  blockedIds: () =>
    req<{ blockedIds: string[] }>('GET', '/friends/blocks'),
  block: (userId: string) =>
    req<{ ok: true; blocked: boolean }>('POST', `/friends/block/${userId}`),
  unblock: (userId: string) =>
    req<{ ok: true; blocked: boolean }>('DELETE', `/friends/block/${userId}`),
  report: (userId: string, reason: string, context?: string) =>
    req<{ ok: true }>('POST', `/friends/report/${userId}`, { reason, context }),
};
